import * as React from 'react';
import { useId, useState } from 'react';
import { Eye, EyeOff, Search, X } from '../Icon/icons';
import { Icon } from '../Icon/Icon';
import {
  wrapper,
  labelCls,
  field,
  nativeInput,
  helperText,
  inlineIconButton,
  skeleton as skeletonCls,
} from './Input.css';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputVariant = 'default' | 'search';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helper?: string;
  /**
   * Error caption. Its presence is the single, uniform way to mark a field
   * invalid across the form family (Input / Textarea / Select / Checkbox /
   * Switch) and is what drives the alarm border and `aria-invalid` — there is
   * no separate public `invalid?` boolean (L26).
   */
  error?: string;
  variant?: InputVariant;
  size?: InputSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Show an X to clear when there's a value. */
  clearable?: boolean;
  /** For type="password" — show an eye icon to toggle visibility. */
  passwordToggle?: boolean;
  /**
   * Loading placeholder: replaces the field with an inert shimmer bar
   * (the label, if any, is rendered `aria-hidden`) while the field's
   * value is still being fetched.
   *
   * Distinct from Button's `loading` prop: `skeleton` means "this
   * control's data hasn't arrived yet" (an inert placeholder); Button's
   * `loading` means "an action this control triggered is in flight" (a
   * live, disabled control). They are intentionally different concepts,
   * not aliases.
   */
  skeleton?: boolean;
  /**
   * Called when the clear (✕) button is pressed. The button only shows
   * when `clearable` is set and the field has a value. In uncontrolled
   * mode the field also resets its own value to empty; in controlled
   * mode the consumer must clear `value` in this handler.
   */
  onClear?: () => void;
  /** Stretch to fill the parent. Use this prop, not `style={{ width: '100%' }}`. */
  fullWidth?: boolean;
  /** React 19 ref-as-prop (replaces the old forwardRef pattern). */
  ref?: React.Ref<HTMLInputElement>;
}

/**
 * Input — text field with optional label, helper / error caption, and
 * inline affordances (search icon, clear ✕, password show/hide eye).
 * Auto-associates label, helper, and `aria-describedby` via a generated
 * id; supports controlled (`value` + `onChange`) and uncontrolled
 * (`defaultValue`) modes. Pass `skeleton` while data is loading to
 * render a flat placeholder bar instead of the field.
 */
export function Input({
  label,
  helper,
  error,
  variant = 'default',
  size = 'md',
  leftIcon,
  rightIcon,
  clearable = false,
  passwordToggle = false,
  skeleton = false,
  onClear,
  fullWidth = false,
  id,
  type: typeProp = 'text',
  disabled,
  value,
  defaultValue,
  onChange,
  placeholder,
  style,
  className,
  ref,
  ...rest
}: InputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const helperId = `${inputId}-helper`;

  const [showPw, setShowPw] = useState(false);
  // Seed from defaultValue, coercing number values (e.g. defaultValue={5}) to
  // their string form so the clear-button visibility check works (M13).
  const [uncontrolled, setUncontrolled] = useState<string>(
    defaultValue != null ? String(defaultValue) : '',
  );
  const isControlled = value !== undefined;
  // `currentValue` only drives the clear-button visibility heuristic — coerce
  // controlled number values (e.g. value={0}) to a string instead of the
  // unsafe `as string` cast, which mis-treated 0 as falsy and broke clearing.
  const currentValue = isControlled ? String(value) : uncontrolled;

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (!isControlled) setUncontrolled(e.currentTarget.value);
    onChange?.(e);
  };

  const type = passwordToggle && showPw ? 'text' : typeProp;

  const resolvedLeftIcon = leftIcon ? (
    leftIcon
  ) : variant === 'search' ? (
    <Icon as={Search} size="sm" />
  ) : null;

  const wrapperCls = [wrapper({ fullWidth }), className].filter(Boolean).join(' ');

  if (skeleton) {
    return (
      <div className={wrapperCls} style={style}>
        {label && (
          <span className={labelCls} aria-hidden="true">
            {label}
          </span>
        )}
        <div className={skeletonCls({ size })} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={wrapperCls} style={style}>
      {label && (
        <label className={labelCls} htmlFor={inputId}>
          {label}
        </label>
      )}
      <div
        className={field({
          size,
          variant,
          error: Boolean(error),
          disabled: Boolean(disabled),
        })}
      >
        {resolvedLeftIcon}
        <input
          ref={ref}
          id={inputId}
          type={type}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          placeholder={placeholder}
          onChange={handleChange}
          className={nativeInput({ size })}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={helper || error ? helperId : undefined}
          {...rest}
        />
        {clearable && currentValue && !disabled && (
          <button
            type="button"
            aria-label="Clear"
            className={inlineIconButton}
            onClick={() => {
              if (!isControlled) setUncontrolled('');
              onClear?.();
            }}
          >
            <Icon as={X} size={14} strokeWidth={2} />
          </button>
        )}
        {passwordToggle && typeProp === 'password' && (
          <button
            type="button"
            aria-label={showPw ? 'Hide password' : 'Show password'}
            aria-pressed={showPw}
            className={inlineIconButton}
            onClick={() => setShowPw((v) => !v)}
          >
            <Icon as={showPw ? EyeOff : Eye} size={14} strokeWidth={1.75} />
          </button>
        )}
        {rightIcon}
      </div>
      {(helper || error) && (
        <span id={helperId} className={helperText({ error: Boolean(error) })}>
          {error ?? helper}
        </span>
      )}
    </div>
  );
}
Input.displayName = 'Input';
